import { DateTime } from 'luxon';

// The sync cutoff and the view-index repairs: how far back the priority sync reaches, when a
// single view's index may be rebuilt, and the one-time rebuild of every view.
//
// These live outside DatabaseWrapper because that class imports pouchdb-browser, which
// references `self` and cannot be loaded under node, so anything that wants a unit test is
// extracted into a module of its own. DatabaseWrapper delegates to them.

/**
 * The cutoff date for the "priority sync" window: log entries at or after this
 * date sync during the initial (catch-up) sync; older ones follow in the
 * background. The cutoff is part of the initial-sync selector, and PouchDB
 * hashes the selector into the replication-checkpoint identity — so every
 * change of this value throws the sync bookmark away and forces a full
 * re-walk of the changes feed (#4219 boot freeze).
 */
export function computePrioritySyncCutoff(nowUtc: DateTime, monthsBack: number): string {
  // floor to the month so the value (and with it the checkpoint identity) is
  // stable within a calendar month: the daily catch-up sync then resumes from
  // its bookmark instead of re-walking the whole history (#4219). the floor
  // widens the window to between `monthsBack` and `monthsBack + 1` months,
  // which is fine — the window is a minimum, not an exact size.
  return nowUtc.minus({ months: monthsBack }).startOf('month').toISODate();
}

/** Result of deciding whether a view-heal salt bump may run now (#4219). */
export interface ViewHealBumpDecision {
  shouldBump: boolean;
  /** the new salt value to store and render, present only when bumping */
  newSalt?: number;
}

/**
 * Decide whether a view index heal (full rebuild via a salt bump) may run now.
 * A heal is a full index rebuild — it must never loop, so at most one bump per
 * view per `minHoursBetweenBumps`. An unparsable last-bump timestamp counts as
 * "no prior bump" (refusing forever on corrupt state would strand a broken
 * index with no heal).
 */
export function decideViewHealBump(
  currentSalt: number | undefined,
  lastBumpUtcIso: string | undefined,
  nowUtc: DateTime,
  minHoursBetweenBumps: number,
): ViewHealBumpDecision {
  if (lastBumpUtcIso) {
    const lastBump = DateTime.fromISO(lastBumpUtcIso);
    // if the previous bump is recent, refuse — the rebuilt index gets a full
    // day to prove itself (and the raw-docs cross-check keeps results correct
    // in the meantime)
    if (lastBump.isValid && nowUtc.diff(lastBump).as('hours') < minHoursBetweenBumps) {
      return { shouldBump: false };
    }
  }
  return { shouldBump: true, newSalt: (currentSalt || 0) + 1 };
}

/** Result of deciding whether the one-time global view rebuild must run (#4343). */
export interface GlobalViewRebuildDecision {
  shouldRebuild: boolean;
  /** the full salts map to store (every registered view bumped once), present only when rebuilding */
  newSalts?: Record<string, number>;
}

/**
 * The one-time global view-rebuild version. (#4343, exported for #4981 phase 3)
 *
 * Bumping it makes every device on the capacitor-sqlite adapter rebuild ALL of its
 * view indexes from scratch, exactly once per database.
 *
 * **It lives here rather than on `DatabaseWrapper` because TWO places now need it and
 * only one of them can load that class.** `DatabaseWrapper` reaches
 * `pouchdb-browser`, which does not load under plain node; the phase 3 migration is
 * node-tested and stamps this version into each migrated database's
 * `_local/view-heal` at the moment it writes the engine marker. That stamp is what
 * exempts a migrated Android device from a rebuild it does not need: it has never run
 * the broken iOS changes feed this repairs, so without the stamp its first SQLite boot
 * — the most expensive boot that device will ever have — would rebuild every view
 * index and file a report, to repair damage that cannot exist.
 *
 * Hard-coding the number in the migration instead would silently drift the next time
 * this is bumped.
 */
export const GLOBAL_VIEW_REBUILD_VERSION = 1;

/**
 * Decide whether the one-time global view rebuild must run on this device
 * (#4343). The iOS SQLite adapter's changes feed used to let the map/reduce
 * checkpoint skip past docs, so devices that ran the broken feed can carry
 * views that are permanently missing documents. Once per version bump of
 * `currentVersion`, every registered view's heal salt is bumped by one, which
 * gives each view a fresh index store that rebuilds from scratch on its next
 * query — correct now that the feed is fixed. Pure so it is unit-testable;
 * the 24h per-view heal rate limit does NOT apply here (different mechanism,
 * runs once per device per version).
 */
export function decideGlobalViewRebuild(
  storedVersion: number | undefined,
  currentVersion: number,
  existingSalts: Record<string, number> | undefined,
  viewNames: string[],
): GlobalViewRebuildDecision {
  if ((storedVersion || 0) >= currentVersion) {
    return { shouldRebuild: false };
  }
  const newSalts: Record<string, number> = { ...(existingSalts || {}) };
  for (const viewName of viewNames) {
    newSalts[viewName] = (newSalts[viewName] || 0) + 1;
  }
  return { shouldRebuild: true, newSalts };
}
